import * as React from "react";
import { Icon, PopoverInteractionKind } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import CircularProgressBar from "../circular-progress-bar";
import QualityTag from "../quality-tag";
import { depthFormatter, percentageFormatter } from "../../utils/metadata-formatter";
import { AssemblyDerivedMetadata, Genome } from "../../api";
import { getGenomeStats } from "../../utils/qc-statistics";

export enum QualityControlType {
  Assembly,
  Busco,
  Illumina,
}

export enum QualityStatus {
  Passed,
  Failed,
  Caution,
  Missing,
}

interface Props {
  type: QualityControlType;
  header: string;
  description: string;
  genome: Genome;
  collectionName: string | null;
}

function getStatus(
  value: number | null | undefined,
  firstBound: number,
  secondBound: number
): QualityStatus {
  if (value === null || value === undefined) {
    return QualityStatus.Missing;
  }
  const lowerIsBetter = firstBound < secondBound;

  if (lowerIsBetter) {
    if (value <= firstBound) {
      return QualityStatus.Passed;
    }
    if (value <= secondBound) {
      return QualityStatus.Caution;
    }
    return QualityStatus.Failed;
  }

  if (value >= firstBound) {
    return QualityStatus.Passed;
  }
  if (value >= secondBound) {
    return QualityStatus.Caution;
  }

  return QualityStatus.Failed;
}

export class QualityControlSection extends React.PureComponent<{
  children: React.ReactNode;
  header: string;
}> {
  render() {
    const { children, header } = this.props;

    return (
      <div className="quality-control__section">
        <h2>{header}</h2>
        {children}
      </div>
    );
  }
}

// NOTE - these cutoffs for PASSED, CAUTION, and FAILED are magic numbers based on conversations with Austin
export class QualityControlGroup extends React.PureComponent<Props> {
  getOcxStats(): AssemblyDerivedMetadata["qc_statistics"] | null {
    return this.props.genome.primary_assembly?.derived_metadata.qc_statistics ?? null;
  }

  getIlluminaStats(): { rows: React.ReactNode; checks: QualityStatus[] } {
    const genomeStats = getGenomeStats(this.props.genome);
    if (genomeStats === null) {
      return { rows: null, checks: [] };
    }

    const { trimmedReads, medianQScore, medianQScorePercent, ambiguousContent } =
      genomeStats.qualityControl.sequencing;

    const trimmedReadsStatus = getStatus(trimmedReads, 1000000, 500000);
    const medianQScoreStatus = getStatus(medianQScore, 30, 30);
    const medianQScorePercentStatus = getStatus(medianQScorePercent, 1.0, 1.0);
    const ambiguousContentStatus = getStatus(ambiguousContent, 5, 10);
    const isViral = this.props.collectionName === "virology";

    const rows = (
      <>
        {!isViral && this.renderStat("Number of trimmed reads", trimmedReads, trimmedReadsStatus)}
        {this.renderStat("Median Q score, all bases", medianQScore, medianQScoreStatus)}
        {medianQScorePercent !== null &&
          this.renderStat(
            "Percent of median Q scores per cycle greater than 25",
            percentageFormatter(medianQScorePercent),
            medianQScorePercentStatus
          )}
        {ambiguousContent !== null &&
          this.renderStat(
            "Ambiguous content (% N bases)",
            ambiguousContent !== null ? percentageFormatter(ambiguousContent!) : "-",
            ambiguousContentStatus
          )}
      </>
    );
    const checks = [medianQScoreStatus, medianQScorePercentStatus, ambiguousContentStatus];
    if (!isViral) {
      checks.push(trimmedReadsStatus);
    }

    return {
      rows,
      checks: checks,
    };
  }

  getAssemblyStats(): { rows: React.ReactNode; checks: QualityStatus[] } {
    const genomeStats = getGenomeStats(this.props.genome);
    if (genomeStats?.qualityControl.assembly === undefined) {
      return { rows: null, checks: [] };
    }

    const { contamination, completeness, depth } = genomeStats.qualityControl.assembly;

    const hasDepth = depth !== null;

    // NOTE - contamination and completeness currently being returned as numerical representation of stringified percentages: 100.0 vs. 1.0
    const completenessStatus = getStatus(completeness, 90, 85);
    const contaminationStatus = getStatus(contamination, 10, 15);
    const depthStatus = hasDepth ? getStatus(depth, 100, 50) : QualityStatus.Missing;

    const rows = (
      <>
        {completeness !== null &&
          this.renderStat(
            "Estimated genome completeness",
            percentageFormatter(completeness / 100),
            completenessStatus
          )}
        {contamination !== null &&
          this.renderStat(
            "Estimated genome contamination",
            percentageFormatter(contamination / 100),
            contaminationStatus
          )}
        {this.renderStat(
          "Average depth of coverage",
          hasDepth ? depthFormatter(depth) : "-",
          depthStatus
        )}
      </>
    );

    return { rows, checks: [completenessStatus, contaminationStatus, depthStatus] };
  }

  getFungalStats(): { rows: React.ReactNode; checks: QualityStatus[] } {
    const genomeStats = getGenomeStats(this.props.genome);
    let depthMean = 0;
    let hasDepth = false;

    if (genomeStats?.sequencingStats?.illumina?.depth?.mean !== undefined) {
      depthMean = genomeStats.sequencingStats.illumina.depth.mean || 0;
      hasDepth = true;
    }

    const depthStatus = hasDepth ? getStatus(depthMean, 100, 50) : QualityStatus.Missing;
    const completeness = genomeStats?.qualityControl?.assembly?.completeness;

    if (!completeness) {
      return { rows: null, checks: [] };
    }

    const completenessStatus = getStatus(completeness, 80, 40);
    const rows = (
      <>
        {this.renderStat(
          "Estimated genome completeness",
          percentageFormatter(completeness / 100),
          completenessStatus
        )}
        {this.renderStat(
          "Average depth of coverage",
          hasDepth ? depthFormatter(depthMean) : "-",
          depthStatus
        )}
      </>
    );

    return { rows, checks: [completenessStatus, depthStatus] };
  }

  getStats(): { rows: React.ReactNode; checks: QualityStatus[] } {
    switch (this.props.type) {
      case QualityControlType.Assembly:
        return this.getAssemblyStats();
      case QualityControlType.Illumina:
        return this.getIlluminaStats();
      case QualityControlType.Busco:
        return this.getFungalStats();
      default:
        throw "Invalid quality type";
    }
  }

  renderStat(name: string, value: number | string | null, status: QualityStatus): React.ReactNode {
    let repr = value;
    if (typeof value === "number") {
      repr = value.toLocaleString();
    }
    const displayText = status !== QualityStatus.Missing ? repr : "–";
    return (
      <div className="quality-control__check">
        <QualityTag status={status} />
        <span className="quality-control__check__name">{name}</span>
        <span className="quality-control__check__result">{displayText}</span>
      </div>
    );
  }

  renderAssemblyInfo() {
    return (
      <Popover2
        interactionKind={PopoverInteractionKind.HOVER}
        placement="top"
        content="CheckM assesses genome quality metrics including completeness and contamination. See Parks 2015"
        popoverClassName="info-tooltip"
      >
        <Icon icon="info-sign" className="info-icon" />
      </Popover2>
    );
  }

  render() {
    const { header, description, type } = this.props;
    const { rows, checks } = this.getStats();

    return (
      <QualityControlSection header={header}>
        <div className="quality-control__description">
          {description}
          {type === QualityControlType.Assembly ? this.renderAssemblyInfo() : null}
        </div>
        <CircularProgressBar checks={checks} />
        <div className="quality-control__checks">{rows}</div>
      </QualityControlSection>
    );
  }
}
